import React from 'react';
import { DatePicker, CurrencyInputField } from '../../../components/common';
import { CategoryDropdown } from '../category';

interface LeaseTermsSectionProps {
  typeId: number;
  beginDate: string;
  endDate: string;
  renewalDate: string;
  leaseAmount: number;
  securityAmount: number;
  isCreate: boolean;
  renewalManuallyCleared: boolean;
  errors: {
    typeId?: string;
    beginDate?: string;
    endDate?: string;
    renewalDate?: string;
    leaseAmount?: string;
  };
  onTypeChange: (typeId: number) => void;
  onBeginDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  onRenewalDateChange: (date: string) => void;
  onRenewalManuallyCleared: (cleared: boolean) => void;
  onLeaseAmountChange: (amount: number) => void;
  onSecurityAmountChange: (amount: number) => void;
}

const LeaseTermsSection: React.FC<LeaseTermsSectionProps> = ({
  typeId,
  beginDate,
  endDate,
  renewalDate,
  leaseAmount,
  securityAmount,
  isCreate,
  renewalManuallyCleared,
  errors,
  onTypeChange,
  onBeginDateChange,
  onEndDateChange,
  onRenewalDateChange,
  onRenewalManuallyCleared,
  onLeaseAmountChange,
  onSecurityAmountChange
}) => {
  const handleEndDateChange = (date: string) => {
    onEndDateChange(date);
    if (isCreate && date && !renewalDate && !renewalManuallyCleared) {
      const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(date.trim());
      if (match) {
        const month = match[1];
        const day = match[2];
        const year = parseInt(match[3], 10) + 1;
        onRenewalDateChange(`${month}/${day}/${year}`);
      }
    }
  };

  const handleRenewalDateChange = (date: string) => {
    onRenewalDateChange(date);
    onRenewalManuallyCleared(date === '');
  };

  return (
    <section className="form-section">
      <h3>Lease Terms</h3>
      <div className="form-section-content">
        <div className="app-form-group">
          <label htmlFor="leaseType" className="required">
            Lease Type
          </label>
          <CategoryDropdown
            parentType="lease"
            value={typeId}
            onChange={(categoryId) => onTypeChange(categoryId)}
            placeholder="Select lease type"
            required
          />
          {errors.typeId && <div className="app-error-message">{errors.typeId}</div>}
        </div>

        <div className="app-form-group">
          <DatePicker
            value={beginDate}
            onChange={onBeginDateChange}
            placeholder="mm/dd/yyyy"
            label="Start Date"
            required
          />
          {errors.beginDate && <div className="app-error-message">{errors.beginDate}</div>}
        </div>

        <div className="app-form-group">
          <DatePicker
            value={endDate}
            onChange={handleEndDateChange}
            placeholder="mm/dd/yyyy"
            label="End Date"
            required
          />
          {errors.endDate && <div className="app-error-message">{errors.endDate}</div>}
        </div>

        <div className="app-form-group">
          <DatePicker
            value={renewalDate}
            onChange={handleRenewalDateChange}
            placeholder="mm/dd/yyyy"
            label="Renewal Date"
          />
          {errors.renewalDate && <div className="app-error-message">{errors.renewalDate}</div>}
        </div>

        <div className="app-form-group">
          <label htmlFor="leaseAmount" className="required">
            Lease Amount
          </label>
          <CurrencyInputField
            value={leaseAmount}
            onChange={onLeaseAmountChange}
            min={0.01}
            max={9999999.99}
            placeholder="Enter lease amount"
          />
          {errors.leaseAmount && <div className="app-error-message">{errors.leaseAmount}</div>}
        </div>

        <div className="app-form-group">
          <label htmlFor="securityAmount">
            Security Deposit
          </label>
          <CurrencyInputField
            value={securityAmount}
            onChange={onSecurityAmountChange}
            min={0}
            max={9999999.99}
            placeholder="Enter security deposit (optional)"
          />
        </div>
      </div>
    </section>
  );
};

export default LeaseTermsSection;
